import { API } from '../../../../../constants/config';
import translations from '../../../../../lang';
import { fetchJson, isAbortError } from '../../../../../utils/fetchJson';

import { TRIAL_START_ENDPOINTS } from './constants';

const { TRIAL_START } = translations['en-US'];

export type TrialStartRequestPayload = {
  email: string;
  recaptcha_token?: string;
  query_string?: string;
};

export class TrialStartApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TrialStartApiError';
  }
}

export type TrialStartResponse = {
  message?: string;
};

export type ValidateTrialEmailResponse = {
  valid: boolean;
  message: string;
};

type FetchJsonInit = NonNullable<Parameters<typeof fetchJson>[1]>;

const isValidateTrialEmailResponse = (
  data: unknown
): data is ValidateTrialEmailResponse =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as ValidateTrialEmailResponse).valid === 'boolean' &&
  ((data as ValidateTrialEmailResponse).message === undefined ||
    typeof (data as ValidateTrialEmailResponse).message === 'string');

/**
 * Public GET — trial start is unauthenticated; do not attach auth headers.
 */
export const validateTrialEmail = async (
  email: string,
  signal?: AbortSignal
): Promise<ValidateTrialEmailResponse> => {
  const fallbackMessage = TRIAL_START.ERRORS.EMAIL_VALIDATE_FAILED;
  const init: FetchJsonInit = {
    method: 'GET',
    signal,
    fallbackMessage,
  };

  try {
    const data = await fetchJson<ValidateTrialEmailResponse>(
      `${API.APP_SERVICES.BASE_URL}${
        TRIAL_START_ENDPOINTS.VALIDATE_EMAIL
      }?email=${encodeURIComponent(email)}`,
      init
    );

    if (!isValidateTrialEmailResponse(data))
      throw new TrialStartApiError(fallbackMessage);

    return data;
  } catch (error) {
    if (isAbortError(error)) throw error;

    throw error instanceof TrialStartApiError
      ? error
      : new TrialStartApiError(fallbackMessage);
  }
};

const readErrorMessage = (data: unknown): string | undefined =>
  data &&
  typeof data === 'object' &&
  typeof (data as { errorMessage?: unknown }).errorMessage === 'string'
    ? (data as { errorMessage: string }).errorMessage
    : undefined;

/**
 * Public POST — kick off trial invite email; do not attach auth headers.
 * Success is any HTTP 2xx response. Never log the payload beyond UI needs.
 */
export const requestTrialStart = async (
  values: TrialStartRequestPayload,
  signal?: AbortSignal
): Promise<TrialStartResponse> => {
  const fallbackMessage = TRIAL_START.ERRORS.START_FAILED;
  const url = `${API.BASE_URL}${TRIAL_START_ENDPOINTS.START}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: values.email.trim(),
        ...(values.recaptcha_token
          ? { recaptcha_token: values.recaptcha_token }
          : {}),
        ...(values.query_string ? { query_string: values.query_string } : {}),
      }),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw new TrialStartApiError(fallbackMessage);
  }

  let data: unknown = {};
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new TrialStartApiError(fallbackMessage);
    }
  }

  if (!response.ok) {
    throw new TrialStartApiError(readErrorMessage(data) || fallbackMessage);
  }

  return typeof (data as { message?: unknown })?.message === 'string'
    ? { message: (data as { message: string }).message }
    : {};
};
